import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { cn } from '@shared/ui/cn';

/**
 * The row's actions, in a native `<details>` / `<summary>` disclosure sharing
 * the account menu's shape.
 *
 * **The panel is `position: fixed`, and that is a fix rather than a
 * preference.** `DTableScroll` carries `overflow-x: auto`, and the
 * specification makes the other axis `auto` too the moment it is not
 * `visible` — so the wrapper clips vertically as well, even with no scrollbar
 * drawn. An absolutely-positioned panel inside a cell was clipped at the
 * table's edge, and the last row's panel was not visible at all. `fixed` takes
 * it out of every clipped containing block, and the two coordinates are written
 * from the handle's own rectangle at open time.
 *
 * The coordinates ride **custom properties**, never the inset properties
 * themselves: the measurement stays in one place and the stylesheet keeps the
 * decision about which edge it is measured from. The arithmetic **reads the
 * document's direction** rather than assuming `rtl`, because the inline-end
 * edge is the left one here and the right one in a `ltr` document.
 *
 * It flips above the handle near the viewport foot, and closes on scroll: a
 * fixed panel does not travel with the row it belongs to, and a menu left
 * hanging over a different row is worse than one that closed.
 *
 * **The flip measures the panel; it does not estimate it.** A single constant
 * standing in for every menu's height put a two-entry panel a fixed 260 px above
 * its handle — the menu opened over rows three above the one it acts on, with
 * clear air between it and the record it belongs to, which reads as a menu
 * pointing at the wrong row. The panel is in the document by the time `onToggle`
 * runs, so its real height is readable there, and the flipped panel's foot is
 * placed on the handle's head.
 *
 * **A destructive entry takes the danger colour inside the panel** — it is the
 * only entry with no way back, and an undifferentiated line beside «حرّر» gets
 * pressed by mistake. Colour is not the only signal: the label says «احذف» and
 * the confirmation after it names the record.
 *
 * **A disabled entry is disabled, not hidden**, and `hint` is the line that
 * says why. Hiding two of five entries on one row and not on another leaves two
 * menus of different lengths with no explanation, which reads as a fault rather
 * than as a rule.
 */

export interface RowMenuEntry {
  /** Stable within one menu; used as the React key and nothing else. */
  key: string;
  label: string;
  icon?: ReactNode | undefined;
  /** The one action with no way back — takes the danger colour in the panel. */
  danger?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Runs after the menu has closed itself, so no panel is left behind a backdrop. */
  onSelect: () => void;
}

/**
 * The panel never sits flush against the viewport edge it flipped away from.
 * One step of the design system's tightest gap, in the one place a token cannot
 * reach: the value is arithmetic on a client rectangle, not a declaration.
 */
const VIEWPORT_GUTTER = 8;

export function RowMenu({
  label,
  entries,
  hint,
}: {
  /** Names the record the menu belongs to — «أفعال خطّة «سنوي»». */
  label: string;
  entries: readonly RowMenuEntry[];
  /** The reason a disabled entry is disabled. Rendered under the entries. */
  hint?: string | undefined;
}) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ block: number; inline: number } | null>(null);

  const close = useCallback(() => {
    const element = detailsRef.current;
    if (element?.open) element.open = false;
  }, []);

  /** Reads the handle's rectangle and resolves the two inset values from it. */
  const place = useCallback(() => {
    const handle = summaryRef.current;
    if (!handle) return;

    const rect = handle.getBoundingClientRect();
    const rtl = getComputedStyle(handle).direction === 'rtl';
    const panel = panelRef.current?.getBoundingClientRect();

    // The inline-end edge is the left one under `rtl` and the right one under
    // `ltr`; the panel is anchored to it either way. The clamp keeps the far
    // edge inside the viewport on a narrow window, where a handle close to the
    // reading start would otherwise push half the entries off screen.
    const anchored = rtl ? rect.left : window.innerWidth - rect.right;
    const inline = Math.max(
      VIEWPORT_GUTTER,
      Math.min(anchored, window.innerWidth - (panel?.width ?? 0) - VIEWPORT_GUTTER),
    );

    // Near the viewport foot the panel opens upward, so the entries stay on
    // screen instead of the operator scrolling to reach a menu they just opened.
    // The height is this panel's own, read now that it is in the document — the
    // flipped panel's foot lands on the handle's head whatever the entry count.
    const panelBlockSize = panel?.height ?? 0;
    const roomBelow = window.innerHeight - rect.bottom - VIEWPORT_GUTTER;
    const block =
      roomBelow < panelBlockSize ? Math.max(VIEWPORT_GUTTER, rect.top - panelBlockSize) : rect.bottom;

    setPosition({ block, inline });
  }, []);

  useEffect(() => {
    if (!position) return;

    // A fixed panel does not travel with its row, so scrolling closes it rather
    // than leaving it pointing at a different record.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [position, close]);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      const element = detailsRef.current;
      if (!element?.open) return;
      if (event.target instanceof Node && element.contains(event.target)) return;
      close();
    }

    /**
     * `Esc` closes every overlay in this console, and it is bound on the
     * document rather than on the disclosure: a `<details>` is not an
     * interactive element, and hanging a key handler on one would put a
     * keyboard contract on a node the accessibility tree does not offer to a
     * keyboard. Focus returns to the handle, which is where it came from.
     */
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (!detailsRef.current?.open) return;
      close();
      summaryRef.current?.focus();
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close]);

  return (
    <details
      ref={detailsRef}
      className="relative"
      onToggle={(event) => {
        if (event.currentTarget.open) place();
        else setPosition(null);
      }}
    >
      <summary
        ref={summaryRef as React.RefObject<HTMLElement>}
        aria-label={label}
        className="hit-target flex cursor-pointer list-none items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink [&::-webkit-details-marker]:hidden"
      >
        <MoreIcon />
      </summary>

      <div
        ref={panelRef}
        style={
          {
            '--menu-block': `${position?.block ?? 0}px`,
            '--menu-inline': `${position?.inline ?? 0}px`,
          } as CSSProperties
        }
        /*
         * The minimum inline size is an arbitrary value rather than `min-w-56`:
         * this repo REPLACES Tailwind's spacing scale with the design system's
         * own steps, and `minWidth` inherits that scale — `min-w-56` names a step
         * that does not exist and emits no declaration at all, which left the
         * panel shrink-wrapped to its longest label.
         */
        className="fixed z-[80] rounded-md border border-border bg-surface-overlay p-2 shadow-4 [inset-block-start:var(--menu-block)] [inset-inline-end:var(--menu-inline)] [inset-inline-start:auto] [min-inline-size:14rem]"
      >
        {entries.map((entry) => (
          <button
            key={entry.key}
            type="button"
            disabled={entry.disabled}
            onClick={() => {
              // The trigger closes its own menu on the way, so nothing is left
              // open behind a dialog's backdrop.
              close();
              entry.onSelect();
            }}
            className={cn(
              'hit-target flex w-full cursor-pointer items-center gap-3 rounded-sm border-0 bg-transparent px-3 text-start text-sm text-ink',
              'hover:bg-hover-bg',
              entry.danger && 'text-danger hover:bg-danger-soft hover:text-danger',
              entry.disabled && 'cursor-not-allowed opacity-45 hover:bg-transparent hover:text-ink-muted',
            )}
          >
            {entry.icon}
            <span>{entry.label}</span>
          </button>
        ))}

        {hint ? (
          <p className="mb-0 mt-2 max-w-none border-t border-border px-3 pb-1 pt-2 text-xs text-ink-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/** The three-dot handle. It has no reading direction and is not mirrored. */
function MoreIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="5" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="12" cy="19" r="1.4" />
    </svg>
  );
}
